import React from 'react';
import { Modal, Button } from 'antd';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import { formatOrderStatusAsPill } from '../../utils/formatOrderStatus';

const rowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
};
const mutedStyle = { color: '#667085', fontSize: '13px' };

const PackageSubscriptionDetailsModal = ({
  visible,
  packageSubscription,
  onClose,
}) => {
  if (!packageSubscription) return null;

  const {
    user,
    packages = [],
    durationMonths,
    packagesSubtotal,
    deliveryFeePerMonth,
    deliveryFeeTotal,
    totalAtPurchase,
    pendingUpgrade,
  } = packageSubscription;

  return (
    <Modal
      className="create_user_modal"
      title="Detalji pretplate na paket"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" className="btn-tertiary" onClick={onClose}>
          Zatvori
        </Button>,
      ]}
    >
      <p style={{ marginBottom: 4 }}>
        Korisnik:{' '}
        <strong>
          {user?.firstName} {user?.lastName}
        </strong>{' '}
        ({user?.email})
      </p>
      <p style={{ marginBottom: 4 }}>Trajanje: {durationMonths} meseci</p>
      <p style={{ marginBottom: 4 }}>
        Napredak: {packageSubscription.fulfilledCycles}/
        {packageSubscription.totalCycles} ciklusa
      </p>
      {packageSubscription.status === 'ACTIVE' &&
        packageSubscription.nextFulfillmentDate && (
          <p style={{ marginBottom: 4 }}>
            Sledeće slanje:{' '}
            {dayjs(packageSubscription.nextFulfillmentDate).format(
              'DD.MM.YYYY.'
            )}
          </p>
        )}

      {/* Contents as bought — a purchase snapshot, unaffected by later edits to the package. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          margin: '20px 0 0',
        }}
      >
        <strong>Paketi (kako su kupljeni)</strong>
        {packages.length > 0 ? (
          packages.map((pkg) => (
            <div
              key={pkg._id}
              style={{
                border: '1px solid #EAECF0',
                borderRadius: '8px',
                padding: '10px 12px',
              }}
            >
              <div style={rowStyle}>
                <span>
                  <strong>{pkg.name}</strong>
                  {pkg.quantity > 1 ? ` × ${pkg.quantity}` : ''}
                </span>
                <span>{formatPriceWithCurrency(pkg.lineTotal ?? 0)}</span>
              </div>
              {pkg.components && pkg.components.length > 0 && (
                <ul
                  style={{
                    margin: '6px 0 0',
                    paddingLeft: '18px',
                    ...mutedStyle,
                  }}
                >
                  {pkg.components.map((c) => (
                    <li key={c._id}>
                      {c.name}
                      {c.quantity > 1 ? ` × ${c.quantity}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))
        ) : (
          <span>-</span>
        )}

        <div style={rowStyle}>
          <span>Međuzbir paketa</span>
          <span>{formatPriceWithCurrency(packagesSubtotal ?? 0)}</span>
        </div>
        <div style={rowStyle}>
          <span>
            Dostava: {formatPriceWithCurrency(deliveryFeePerMonth ?? 0)} ×{' '}
            {durationMonths} meseci
          </span>
          <span>{formatPriceWithCurrency(deliveryFeeTotal ?? 0)}</span>
        </div>
        <div
          style={{
            ...rowStyle,
            borderTop: '1px solid #EAECF0',
            paddingTop: '8px',
          }}
        >
          <strong>Ukupno plaćeno</strong>
          <strong>{formatPriceWithCurrency(totalAtPurchase ?? 0)}</strong>
        </div>
        {pendingUpgrade && (
          <span style={mutedStyle}>
            Nadogradnja na {pendingUpgrade.durationMonths} meseci čeka uplatu:{' '}
            {formatPriceWithCurrency(pendingUpgrade.priceDelta ?? 0)} (paketi{' '}
            {formatPriceWithCurrency(pendingUpgrade.packagesDelta ?? 0)} +
            dostava {formatPriceWithCurrency(pendingUpgrade.deliveryDelta ?? 0)}
            )
          </span>
        )}
      </div>

      <div
        className="create_user_modal_form"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          margin: '20px 0 0',
        }}
      >
        <strong>Porudžbine</strong>
        {packageSubscription.orders && packageSubscription.orders.length > 0 ? (
          packageSubscription.orders.map((order) => (
            <div key={order._id} style={rowStyle}>
              <span>{order.orderNumber}</span>
              {formatOrderStatusAsPill(order.status)}
            </div>
          ))
        ) : (
          <span>Nema porudžbina.</span>
        )}
      </div>
    </Modal>
  );
};

PackageSubscriptionDetailsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  packageSubscription: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default PackageSubscriptionDetailsModal;
